/**
 * @boli/core - Idempotency Key Generator
 *
 * Platform-agnostic idempotency handling for Boli Live.
 * Prevents duplicate operations due to:
 * - Network retries
 * - Double-clicks
 * - Mobile app backgrounding/resuming
 *
 * Used by BOTH Web (Phase 1) and Mobile (Phase 2) with ZERO changes.
 */

// ============================================================================
// CONFIGURATION
// ============================================================================

export interface IdempotencyConfig {
  maxStoredKeys: number;
  keyTtlMs: number; // Time-to-live for keys
  cleanupIntervalMs: number;
}

const DEFAULT_CONFIG: IdempotencyConfig = {
  maxStoredKeys: 1000,
  keyTtlMs: 300000, // 5 minutes
  cleanupIntervalMs: 60000, // Cleanup every minute
};

// ============================================================================
// STORED KEY INTERFACE
// ============================================================================

interface StoredKey {
  key: string;
  createdAt: number;
  expiresAt: number;
  status: 'pending' | 'completed' | 'failed';
}

// ============================================================================
// IDEMPOTENCY SERVICE
// ============================================================================

export class IdempotencyService {
  private config: IdempotencyConfig;
  private keys: Map<string, StoredKey> = new Map();
  private cleanupIntervalId?: ReturnType<typeof setInterval>;

  constructor(config: Partial<IdempotencyConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.startCleanup();
  }

  /**
   * Generate a unique idempotency key for a bid
   * Key format: bid_{auctionId}_{userId}_{amount}_{timestamp}_{random}
   */
  generateBidKey(auctionId: string, userId: string, amount: number): string {
    const timestamp = Date.now();
    const random = this.generateRandomPart();
    const key = `bid_${auctionId}_${userId}_${amount}_${timestamp}_${random}`;

    this.storeKey(key);
    return key;
  }

  /**
   * Generate a unique idempotency key for a payment
   */
  generatePaymentKey(orderId: string, userId: string, amount: number): string {
    const timestamp = Date.now();
    const random = this.generateRandomPart();
    const key = `payment_${orderId}_${userId}_${amount}_${timestamp}_${random}`;

    this.storeKey(key);
    return key;
  }

  /**
   * Generate a generic idempotency key
   */
  generate(prefix: string = 'op'): string {
    const timestamp = Date.now();
    const random = this.generateRandomPart();
    const key = `${prefix}_${timestamp}_${random}`;

    this.storeKey(key);
    return key;
  }

  /**
   * Generate a deterministic key (same inputs = same key)
   * Use for operations that should be deduplicated based on content
   */
  generateDeterministic(...parts: (string | number)[]): string {
    const content = parts.join('_');
    const hash = this.hashString(content);
    const key = `det_${hash}`;

    // Don't auto-store deterministic keys
    return key;
  }

  /**
   * Check if a key has been used
   */
  hasBeenUsed(key: string): boolean {
    const stored = this.keys.get(key);
    if (!stored) return false;

    // Check if expired
    if (Date.now() > stored.expiresAt) {
      this.keys.delete(key);
      return false;
    }

    return stored.status === 'completed';
  }

  /**
   * Check if a key is pending (in-flight)
   */
  isPending(key: string): boolean {
    const stored = this.keys.get(key);
    if (!stored) return false;

    if (Date.now() > stored.expiresAt) {
      this.keys.delete(key);
      return false;
    }

    return stored.status === 'pending';
  }

  /**
   * Mark a key as used/completed
   */
  markAsUsed(key: string): void {
    const stored = this.keys.get(key);
    if (stored) {
      stored.status = 'completed';
    } else {
      this.storeKey(key, 'completed');
    }
  }

  /**
   * Mark a key as failed (allows retry)
   */
  markAsFailed(key: string): void {
    const stored = this.keys.get(key);
    if (stored) {
      stored.status = 'failed';
    }
  }

  /**
   * Get the status of a key
   */
  getStatus(key: string): 'unknown' | 'pending' | 'completed' | 'failed' | 'expired' {
    const stored = this.keys.get(key);
    if (!stored) return 'unknown';

    if (Date.now() > stored.expiresAt) {
      this.keys.delete(key);
      return 'expired';
    }

    return stored.status;
  }

  /**
   * Remove a key
   */
  remove(key: string): void {
    this.keys.delete(key);
  }

  /**
   * Clear all stored keys
   */
  clear(): void {
    this.keys.clear();
  }

  /**
   * Get count of stored keys
   */
  getStoredCount(): number {
    return this.keys.size;
  }

  /**
   * Get all pending keys
   */
  getPendingKeys(): string[] {
    const pending: string[] = [];
    const now = Date.now();

    this.keys.forEach((stored, key) => {
      if (stored.status === 'pending' && now <= stored.expiresAt) {
        pending.push(key);
      }
    });

    return pending;
  }

  /**
   * Cleanup and stop auto-cleanup
   */
  destroy(): void {
    if (this.cleanupIntervalId) {
      clearInterval(this.cleanupIntervalId);
      this.cleanupIntervalId = undefined;
    }
    this.keys.clear();
  }

  // ============================================================================
  // PRIVATE METHODS
  // ============================================================================

  private storeKey(key: string, status: StoredKey['status'] = 'pending'): void {
    const now = Date.now();

    this.keys.set(key, {
      key,
      createdAt: now,
      expiresAt: now + this.config.keyTtlMs,
      status,
    });

    // Evict old keys if limit reached
    if (this.keys.size > this.config.maxStoredKeys) {
      this.evictOldestKeys(100);
    }
  }

  private evictOldestKeys(count: number): void {
    const entries = Array.from(this.keys.entries())
      .sort((a, b) => a[1].createdAt - b[1].createdAt)
      .slice(0, count);

    entries.forEach(([key]) => this.keys.delete(key));
  }

  private startCleanup(): void {
    this.cleanupIntervalId = setInterval(() => {
      this.cleanupExpiredKeys();
    }, this.config.cleanupIntervalMs);
  }

  private cleanupExpiredKeys(): void {
    const now = Date.now();
    const keysToDelete: string[] = [];

    this.keys.forEach((stored, key) => {
      if (now > stored.expiresAt) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach((key) => this.keys.delete(key));
  }

  private generateRandomPart(): string {
    const array = new Uint8Array(8);

    // Use crypto if available, fallback to Math.random
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      crypto.getRandomValues(array);
    } else {
      for (let i = 0; i < array.length; i++) {
        array[i] = Math.floor(Math.random() * 256);
      }
    }

    return Array.from(array)
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  private hashString(str: string): string {
    // Simple hash function (FNV-1a)
    let hash = 2166136261;
    for (let i = 0; i < str.length; i++) {
      hash ^= str.charCodeAt(i);
      hash = Math.imul(hash, 16777619);
    }
    return (hash >>> 0).toString(16).padStart(8, '0');
  }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

let idempotencyInstance: IdempotencyService | null = null;

export function getIdempotencyService(
  config?: Partial<IdempotencyConfig>
): IdempotencyService {
  if (!idempotencyInstance) {
    idempotencyInstance = new IdempotencyService(config);
  }
  return idempotencyInstance;
}

export function destroyIdempotencyService(): void {
  if (idempotencyInstance) {
    idempotencyInstance.destroy();
    idempotencyInstance = null;
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Decorator for making functions idempotent
 */
export function withIdempotency<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  keyGenerator: (...args: Parameters<T>) => string
): T {
  const service = getIdempotencyService();

  return (async (...args: Parameters<T>) => {
    const key = keyGenerator(...args);

    // Check if already completed
    if (service.hasBeenUsed(key)) {
      console.log(`Idempotency: Key ${key} already used, skipping`);
      throw new Error('DUPLICATE_OPERATION');
    }

    // Check if in-flight
    if (service.isPending(key)) {
      console.log(`Idempotency: Key ${key} is pending, waiting`);
      throw new Error('OPERATION_IN_PROGRESS');
    }

    try {
      const result = await fn(...args);
      service.markAsUsed(key);
      return result;
    } catch (error) {
      service.markAsFailed(key);
      throw error;
    }
  }) as T;
}
